import React, { useState } from 'react';
import { Box, Text, ActionIcon, Tooltip, Badge, Group } from '@mantine/core';
import { useMantineColorScheme } from '@mantine/core';
import { IconCopy, IconChevronDown, IconChevronUp } from '@tabler/icons-react';

interface SidDisplayProps {
    value: string;
    variant?: 'compact' | 'expanded' | 'chip';
    showCopyButton?: boolean;
    showExpandButton?: boolean;
    fontFamily?: string;
    fontSize?: string;
}

const SID_REGEX = /^S-1-[0-9]+(-[0-9]+)*$/i;

export const isSid = (value: string): boolean => {
    return SID_REGEX.test(value);
};

export const formatSid = (sid: string): string => {
    if (!isSid(sid)) return sid;
    return sid;
};

import { copyToClipboard } from '../../utils/clipboardUtils';

export const SidDisplay: React.FC<SidDisplayProps> = ({
    value,
    variant = 'compact',
    showCopyButton = true,
    showExpandButton = true,
    fontFamily = 'monospace',
    fontSize = '0.875rem'
}) => {
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';
    const [copied, setCopied] = useState(false);
    const [expanded, setExpanded] = useState(false);

    if (!isSid(value)) {
        return <Text ff={fontFamily} fz={fontSize}>{value}</Text>;
    }

    const formattedSid = formatSid(value);
    const parts = formattedSid.split('-');
    const shortSid = parts.length > 4
        ? `${parts.slice(0, 4).join('-')}...${parts[parts.length - 1]}`
        : formattedSid;

    const handleCopy = async () => {
        const success = await copyToClipboard(formattedSid);
        if (success) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleExpand = () => {
        setExpanded(!expanded);
    };

    if (variant === 'chip') {
        return (
            <Group gap={4} align="center" wrap="nowrap">
                <Badge
                    variant="outline"
                    size="sm"
                    ff={fontFamily}
                    fz="xs"
                    style={{
                        cursor: showExpandButton ? 'pointer' : 'default',
                        maxWidth: expanded ? 'none' : '200px',
                        textTransform: 'none'
                    }}
                    onClick={showExpandButton ? handleExpand : undefined}
                >
                    {expanded ? formattedSid : shortSid}
                </Badge>
                {showCopyButton && (
                    <Tooltip label={copied ? "Copied!" : "Copy SID"}>
                        <ActionIcon variant="subtle" size="sm" onClick={handleCopy} color={copied ? "green" : "gray"}>
                            <IconCopy size={14} />
                        </ActionIcon>
                    </Tooltip>
                )}
            </Group>
        );
    }

    if (variant === 'expanded') {
        return (
            <Group gap="xs" align="center" wrap="nowrap">
                <Text
                    ff={fontFamily}
                    fz={fontSize}
                    px="xs"
                    py={4}
                    style={{
                        backgroundColor: isDark ? 'var(--mantine-color-dark-6)' : 'var(--mantine-color-gray-1)',
                        borderRadius: '4px',
                        border: '1px solid',
                        borderColor: isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-3)'
                    }}
                >
                    {formattedSid}
                </Text>
                {showCopyButton && (
                    <Tooltip label={copied ? "Copied!" : "Copy SID"}>
                        <ActionIcon variant="subtle" size="sm" onClick={handleCopy} color={copied ? "green" : "gray"}>
                            <IconCopy size={14} />
                        </ActionIcon>
                    </Tooltip>
                )}
            </Group>
        );
    }

    return (
        <Group gap="xs" align="center" wrap="nowrap">
            <Text
                ff={fontFamily}
                fz={fontSize}
                px={6}
                py={2}
                style={{
                    backgroundColor: isDark ? 'var(--mantine-color-dark-7)' : 'var(--mantine-color-gray-0)',
                    borderRadius: '3px',
                    border: '1px solid',
                    borderColor: isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-2)',
                    cursor: 'pointer'
                }}
                onClick={handleCopy}
            >
                {expanded ? formattedSid : shortSid}
            </Text>

            {showExpandButton && (
                <Tooltip label={expanded ? "Show less" : "Show full SID"}>
                    <ActionIcon variant="subtle" size="sm" onClick={handleExpand}>
                        {expanded ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
                    </ActionIcon>
                </Tooltip>
            )}

            {showCopyButton && (
                <Tooltip label={copied ? "Copied!" : "Copy SID"}>
                    <ActionIcon variant="subtle" size="sm" onClick={handleCopy} color={copied ? "green" : "gray"}>
                        <IconCopy size={14} />
                    </ActionIcon>
                </Tooltip>
            )}
        </Group>
    );
};
